'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS } from 'react-joyride';
import { useSession } from 'next-auth/react';

interface WelcomeTourProps {
  onComplete?: () => void;
  shouldRun?: boolean;
}

export function WelcomeTour({ onComplete, shouldRun = false }: WelcomeTourProps) {
  const { data: session } = useSession();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);

  // Check if we're on desktop
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024); // lg breakpoint
    };

    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  // Start tour when shouldRun becomes true and we're on desktop
  // Note: Tour now shows beacons but doesn't auto-start - user must click beacon to start
  useEffect(() => {
    if (shouldRun && isDesktop) {
      // Don't auto-start - just set up beacons without running
      // Users must click on beacons to start the tour
      setRun(false);
    }
  }, [shouldRun, isDesktop]);

  const steps: Step[] = [
    {
      target: 'body',
      content: (
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-gray-900">Welcome to USLand!</h2>
          <p className="text-gray-700">
            Let&apos;s take a quick tour of the platform&apos;s key features.
            This will only take a minute.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mt-2">
            <p className="text-xs text-blue-900">
              <strong>Note:</strong> Some tour steps may be skipped if certain features aren&apos;t currently visible (like the messaging widget when you have no active conversations).
            </p>
          </div>
          <p className="text-sm text-gray-500">
            You can skip this tour at any time by clicking &quot;Skip&quot; or pressing ESC.
          </p>
        </div>
      ),
      placement: 'center',
      // Beacon enabled so users see flashing dots for points of interest
      disableBeacon: false,
    },
    {
      target: '.left-sidebar-nav',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Navigation Sidebar</h3>
          <p className="text-gray-700">
            Access all major sections here: Home, Search, Services, Pricing, and Contact.
          </p>
          <p className="text-sm text-gray-600">
            Click the arrow button to collapse or expand the sidebar.
          </p>
        </div>
      ),
      placement: 'right',
      spotlightPadding: 10,
    },
    {
      target: '[data-tour="search-button"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Property Search</h3>
          <p className="text-gray-700">
            Find commercial properties using our advanced search and interactive map.
          </p>
          <p className="text-sm text-gray-600">
            Filter by property type, location, price, and more.
          </p>
        </div>
      ),
      placement: 'right',
      spotlightPadding: 5,
    },
    {
      target: '[data-tour="contact-button"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Contact Us</h3>
          <p className="text-gray-700">
            Have questions or need help? Contact our support team.
          </p>
          <p className="text-sm text-gray-600">
            We&apos;re here to help you find the perfect commercial property.
          </p>
        </div>
      ),
      placement: 'right',
      spotlightPadding: 5,
    },
    {
      target: '[data-tour="global-search"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Global Search</h3>
          <p className="text-gray-700">
            Quickly search properties from anywhere using AI-powered natural language search.
          </p>
          <div className="bg-violet-50 border border-violet-200 rounded-md p-2 mt-2">
            <p className="text-xs text-violet-900">
              <strong>Try:</strong> &quot;Office space in downtown&quot; or &quot;warehouse near highway&quot;
            </p>
          </div>
          <p className="text-sm text-gray-600">
            Search by location, property type, or even by property ID (e.g., USL-12345).
          </p>
        </div>
      ),
      placement: 'bottom',
      spotlightPadding: 5,
    },
    {
      target: '[data-tour="tradesmen-button"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Tradesmen Directory</h3>
          <p className="text-gray-700">
            Find trusted contractors and service providers for your commercial property needs.
          </p>
          <div className="bg-orange-50 border border-orange-200 rounded-md p-2 mt-2">
            <p className="text-xs text-orange-900">
              <strong>Categories:</strong> Roofing, HVAC, Plumbing, Electrical, and more!
            </p>
          </div>
          <p className="text-sm text-gray-600">
            Browse by category or search for specific services.
          </p>
        </div>
      ),
      placement: 'right',
      spotlightPadding: 5,
    },
    {
      target: '[data-tour="help-button"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Help Center</h3>
          <p className="text-gray-700">
            Access tutorials, FAQs, and support articles to get the most out of USLand.
          </p>
          <div className="bg-indigo-50 border border-indigo-200 rounded-md p-2 mt-2">
            <p className="text-xs text-indigo-900">
              <strong>Tip:</strong> Use Cmd+K (Mac) or Ctrl+K (Windows) to open quick search!
            </p>
          </div>
          <p className="text-sm text-gray-600">
            Find answers to common questions or contact support.
          </p>
        </div>
      ),
      placement: 'right',
      spotlightPadding: 5,
    },
    {
      target: '.right-sidebar-tabs-container',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Quick Access Panel</h3>
          <p className="text-gray-700">
            View your recently viewed listings, saved favorites, and notifications.
          </p>
          <p className="text-sm text-gray-600">
            This panel is always accessible from any page.
          </p>
        </div>
      ),
      placement: 'left',
      spotlightPadding: 10,
    },
    {
      target: '[data-tour="header-messages"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">💬 Messages Icon</h3>
          <p className="text-gray-700">
            Quick access to your messages from the top header. Click to view all conversations.
          </p>
          <div className="bg-cyan-50 border border-cyan-200 rounded-md p-2 mt-2">
            <p className="text-xs text-cyan-900">
              <strong>Tip:</strong> You&apos;ll see a badge notification when you have unread messages.
            </p>
          </div>
        </div>
      ),
      placement: 'bottom',
      spotlightPadding: 5,
    },
    {
      target: '[data-tour="messaging-widget"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">💬 Real-Time Messaging Widget</h3>
          <p className="text-gray-700">
            When you have active conversations, the messaging widget appears here for quick access.
          </p>
          <p className="text-sm text-gray-600">
            Communicate directly with property owners and brokers in real-time!
          </p>
          <div className="bg-cyan-50 border border-cyan-200 rounded-md p-2 mt-2 space-y-1">
            <p className="text-xs text-cyan-900">
              <strong>💡 Features:</strong>
            </p>
            <ul className="text-xs text-cyan-800 list-disc list-inside">
              <li>Instant message delivery</li>
              <li>Email notifications when offline</li>
              <li>Messages linked to properties</li>
              <li>Real-time typing indicators</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>Click the info (ℹ️) button</strong> in the widget header to learn more!
          </p>
        </div>
      ),
      placement: 'left',
      spotlightPadding: 10,
    },
    {
      target: '[data-tour="help-chat-button"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">💬 AI Chat Support</h3>
          <p className="text-gray-700">
            Need instant help? Chat with our AI assistant for quick answers and support.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mt-2">
            <p className="text-xs text-blue-900">
              <strong>💡 Features:</strong>
            </p>
            <ul className="text-xs text-blue-800 list-disc list-inside">
              <li>24/7 instant AI assistance</li>
              <li>Get answers to common questions</li>
              <li>Escalate to human support if needed</li>
              <li>Context-aware help</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            Click the floating chat button anytime to get help!
          </p>
        </div>
      ),
      placement: 'left',
      spotlightPadding: 5,
    },
    {
      target: '[data-tour="user-menu"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">User Menu</h3>
          <p className="text-gray-700">
            Access your profile, settings, dashboard, and account preferences here.
          </p>
          {session && (
            <p className="text-sm text-blue-600 font-medium">
              Welcome, {session.user?.name || 'User'}!
            </p>
          )}
        </div>
      ),
      placement: 'bottom',
      spotlightPadding: 5,
    },
    {
      target: '[data-tour="bug-report"]',
      content: (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-red-600">🐛 Bug Report Button (BETA)</h3>
          <p className="text-gray-700">
            <strong>This is important!</strong> Found something that&apos;s not working right? Please report it!
          </p>
          <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-2">
            <p className="text-sm font-medium text-red-900">How to report a bug:</p>
            <ol className="text-sm text-red-800 list-decimal list-inside space-y-1">
              <li>Click the pulsing bug icon (🐛) button</li>
              <li>Describe what went wrong in detail</li>
              <li>Select the category (Bug, Feature Request, Improvement, etc.)</li>
              <li>Submit - we&apos;ll get notified immediately via email!</li>
            </ol>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-md p-2 mt-2">
            <p className="text-xs text-orange-900 font-medium">
              🎯 <strong>Beta Testers:</strong> Your feedback is crucial! Please report any issues you encounter.
            </p>
          </div>
          <p className="text-sm text-gray-600">
            Click the <strong>&quot;Why report bugs?&quot;</strong> info link below to learn more!
          </p>
        </div>
      ),
      placement: 'right',
      spotlightPadding: 8,
    },
    {
      target: 'body',
      content: (
        <div className="space-y-3">
          <h2 className="text-2xl font-bold text-gray-900">You&apos;re All Set! 🎉</h2>
          <p className="text-gray-700">
            You now know the basics of USLand. Start exploring commercial properties!
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2">
            <p className="text-sm font-medium text-blue-900">Quick Tips:</p>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Use <strong>global search</strong> (header) for quick property searches</li>
              <li>• Explore the <strong>Tradesmen directory</strong> for contractors &amp; services</li>
              <li>• Visit the <strong>Help Center</strong> or use <strong>Cmd+K</strong> for help</li>
              <li>• Chat with <strong>AI support</strong> using the floating chat button</li>
              <li>• Save favorites to review later in the Quick Access Panel</li>
              <li>• Access messages from the header icon or messaging widget</li>
              <li>• Contact property owners through real-time messaging</li>
              <li>• <strong>Report bugs using the pulsing bug button</strong></li>
              <li>• <strong>Click info (ℹ️) buttons</strong> to learn more about features</li>
            </ul>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-md p-2 mt-2">
            <p className="text-xs text-green-900 font-medium">
              💡 Look for <strong>&quot;Why report bugs?&quot;</strong> below the bug button and <strong>info buttons</strong> throughout the app to learn more!
            </p>
          </div>
          <p className="text-sm text-gray-500">
            You can restart this tour anytime from the user menu &gt; Restart Tour.
          </p>
        </div>
      ),
      placement: 'center',
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action, index, type } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      // Tour completed or skipped
      setRun(false);

      if (status === STATUS.FINISHED) {
        localStorage.setItem('usland-tour-completed', 'true');
        localStorage.removeItem('usland-tour-skipped');
      } else if (status === STATUS.SKIPPED) {
        localStorage.setItem('usland-tour-skipped', 'true');
      }

      onComplete?.();
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Update step index
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setStepIndex(nextStepIndex);
    }
  };

  // Public method to restart tour
  const restartTour = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.removeItem('usland-tour-completed');
      localStorage.removeItem('usland-tour-skipped');
    }
    setStepIndex(0);
    setRun(true);
  }, []);

  // Expose restart method via custom event
  useEffect(() => {
    const handleRestartTour = () => {
      // Only restart on desktop (mobile uses its own tour)
      if (isDesktop) {
        restartTour();
      }
    };

    window.addEventListener('restart-welcome-tour', handleRestartTour);
    return () => window.removeEventListener('restart-welcome-tour', handleRestartTour);
  }, [isDesktop, restartTour]);

  // Don't render anything on mobile (mobile uses MobileWelcomeTour)
  if (!isDesktop) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#0066cc',
          textColor: '#1f2937',
          backgroundColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          arrowColor: '#ffffff',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 8,
          padding: 20,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#0066cc',
          fontSize: 14,
          fontWeight: 600,
          padding: '10px 20px',
          borderRadius: 6,
        },
        buttonBack: {
          color: '#6b7280',
          fontSize: 14,
          marginRight: 10,
        },
        buttonSkip: {
          color: '#9ca3af',
          fontSize: 14,
        },
        spotlight: {
          borderRadius: 8,
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        open: 'Open',
        skip: 'Skip tour',
      }}
      floaterProps={{
        disableAnimation: false,
      }}
      disableScrolling
      disableOverlayClose
      spotlightClicks
    />
  );
}

// Helper function to restart tour from anywhere
export function restartWelcomeTour() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('restart-welcome-tour'));
  }
}
